import { Route, Navigate } from "react-router";
import { DmTokenBridge } from "src/dm/utils/DmTokenBridge";
import AppLayout from "src/dm/components/AppLayout/AppLayout";
import DealLayout from "src/dm/components/DealLayout/DealLayout";
import RouteErrorBoundary from "src/dm/components/RouteErrorBoundary/RouteErrorBoundary";
import DashboardPage from "src/dm/pages/Dashboard/DashboardPage";
import DealAttachmentsPage from "src/dm/pages/DealAttachmentsPage/DealAttachmentsPage";
import DealEditPage from "src/dm/pages/DealEdit/DealEdit";
import DealErrorsPage from "src/dm/pages/DealErrorsPage/DealErrorsPage";
import DealFeesPage from "src/dm/pages/DealFeesPage/DealFeesPage";
import DealHeaderPage from "src/dm/pages/DealHeaderPage/DealHeaderPage";
import DealHistoryPage from "src/dm/pages/DealHistoryPage/DealHistoryPage";
import DealItemDetailPage from "src/dm/pages/DealItemDetailPage/DealItemDetailPage";
import DealItemsPage from "src/dm/pages/DealItemsPage/DealItemsPage";
import DealLinkPage from "src/dm/pages/DealLinkPage/DealLinkPage";
import DealNotesPage from "src/dm/pages/DealNotesPage/DealNotesPage";
import DealPromoGroupsPage from "src/dm/pages/DealPromoGroupsPage/DealPromoGroupsPage";
import EditReward from "src/dm/pages/EditReward/EditReward";
import EditRewardItems from "src/dm/pages/EditRewardItems/EditRewardItems";
import EditRewardBilling from "src/dm/pages/EditRewardBilling/EditRewardBilling";
import ViewReward from "src/dm/pages/ViewReward/ViewReward";
import DealSignaturesPage from "src/dm/pages/DealSignaturesPage/DealSignaturesPage";
import NewDealPage from "src/dm/pages/NewDeal/NewDeal";
import CopyDeal from "src/dm/pages/CopyDeal/CopyDeal";
import AddItem from "src/dm/pages/AddItem/AddItem";
import BulkEditItems from "src/dm/pages/BulkEditItems/BulkEditItems";
import AdvancedSearch from "src/dm/pages/AdvancedSearch/AdvancedSearch";
import WarningsAndErrorsPage from "src/dm/pages/WarningsAndErrors/WarningsAndErrors";
import AdminLanding from "src/dm/pages/Admin/AdminLanding";
import AdminLookupPage from "src/dm/pages/Admin/AdminLookupPage";
import DealMappingsPage from "src/dm/pages/Admin/DealMappingsPage";
import DealOwnerChangesPage from "src/dm/pages/Admin/DealOwnerChangesPage";
import UserInformationPage from "src/dm/pages/Admin/UserInformationPage";
import TermsPage from "src/dm/pages/Admin/TermsPage";
import PreferencesPage from "src/dm/pages/Preferences/Preferences";
import AuditReport from "src/dm/pages/AuditReport/AuditReport";
import CategoryTeamPrefs from "src/dm/pages/CategoryTeamPrefs/CategoryTeamPrefs";
import DashboardCustomizationPage from "src/dm/pages/DashboardCustomization/DashboardCustomization";
import {
  ManageItemCache,
  ManageLoadFailedDeals,
} from "src/dm/pages/OperationalUtilities/Pages";

// DM routes — mounted under /dm as a SIBLING of the main RP route tree
// (see GE.DFE.RP src/utils/routesConfig.tsx). DM keeps its own AppLayout
// chrome; no RP nav wraps these screens. The /dm surface is intentionally
// not linked from RP's nav — reachable by URL only.
export const dmRoutes = (
  <Route
    path="/dm"
    element={
      <DmTokenBridge>
        <AppLayout />
      </DmTokenBridge>
    }
    errorElement={<RouteErrorBoundary />}
  >
    <Route index element={<DashboardPage />} errorElement={<RouteErrorBoundary />} />
    <Route
      path="search"
      element={<AdvancedSearch />}
      handle={{ crumb: "Search" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="new"
      element={<NewDealPage />}
      handle={{ crumb: "New Deal" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="deals/:dealId/edit"
      element={<DealEditPage />}
      handle={{
        crumb: (m: { params: { dealId?: string } }) =>
          `Deal #${m.params.dealId ?? "?"} · Edit`,
      }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="deals/:dealId/copy"
      element={<CopyDeal />}
      handle={{
        crumb: (m: { params: { dealId?: string } }) =>
          `Deal #${m.params.dealId ?? "?"} · Copy`,
      }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="deals/:dealId"
      element={<DealLayout />}
      errorElement={<RouteErrorBoundary />}
      handle={{
        crumb: (m: { params: { dealId?: string } }) =>
          `Deal #${m.params.dealId ?? "?"}`,
      }}
    >
      <Route index element={<DealHeaderPage />} errorElement={<RouteErrorBoundary />} />
      <Route
        path="items"
        element={<DealItemsPage />}
        handle={{ crumb: "Items" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route path="items/add" element={<AddItem />} errorElement={<RouteErrorBoundary />} />
      <Route
        path="items/bulk-edit"
        element={<BulkEditItems />}
        handle={{ crumb: "Bulk Edit Items" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="items/:dealItemId"
        element={<DealItemDetailPage />}
        handle={{
          crumb: (m: { params: { dealItemId?: string } }) =>
            `Item ${m.params.dealItemId ?? "?"}`,
        }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="notes"
        element={<DealNotesPage />}
        handle={{ crumb: "Notes" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="attachments"
        element={<DealAttachmentsPage />}
        handle={{ crumb: "Attachments" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="history"
        element={<DealHistoryPage />}
        handle={{ crumb: "History" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="signatures"
        element={<DealSignaturesPage />}
        handle={{ crumb: "Signatures" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="errors"
        element={<DealErrorsPage />}
        handle={{ crumb: "Errors" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="warnings"
        element={<WarningsAndErrorsPage />}
        handle={{ crumb: "Warnings" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="fees"
        element={<DealFeesPage />}
        handle={{ crumb: "Fees" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="reward"
        element={<EditReward />}
        handle={{ crumb: "Reward" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="reward/view"
        element={<ViewReward />}
        handle={{ crumb: "Reward · View" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="reward/items"
        element={<EditRewardItems />}
        handle={{ crumb: "Reward · Items" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="reward/billing"
        element={<EditRewardBilling />}
        handle={{ crumb: "Reward · Billing" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route path="rewards" element={<Navigate to=".." relative="path" replace />} />
      <Route path="rewards/*" element={<Navigate to="../reward" relative="path" replace />} />
      <Route
        path="promo-groups"
        element={<DealPromoGroupsPage />}
        handle={{ crumb: "Promo Groups" }}
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="link"
        element={<DealLinkPage />}
        handle={{ crumb: "Link" }}
        errorElement={<RouteErrorBoundary />}
      />
    </Route>
    <Route
      path="admin"
      element={<AdminLanding />}
      handle={{ crumb: "Admin" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="admin/deal-mappings"
      element={<DealMappingsPage />}
      handle={{ crumb: "Admin · Deal Mappings" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="admin/deal-owner-changes"
      element={<DealOwnerChangesPage />}
      handle={{ crumb: "Admin · Deal Owner Changes" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="admin/user-information"
      element={<UserInformationPage />}
      handle={{ crumb: "Admin · User Information" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="admin/terms"
      element={<TermsPage />}
      handle={{ crumb: "Admin · Terms" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="admin/:name"
      element={<AdminLookupPage />}
      handle={{
        crumb: (m: { params: { name?: string } }) =>
          `Admin · ${m.params.name ?? "Lookup"}`,
      }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="preferences"
      element={<PreferencesPage />}
      handle={{ crumb: "Preferences" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="preferences/dashboard"
      element={<DashboardCustomizationPage />}
      handle={{ crumb: "Preferences · Customize Dashboard" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route path="dashboard-customization" element={<Navigate to="/dm/preferences/dashboard" replace />} />
    <Route
      path="audit"
      element={<AuditReport />}
      handle={{ crumb: "Audit" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="category-team-preferences"
      element={<CategoryTeamPrefs />}
      handle={{ crumb: "Category Team Preferences" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="manage-item-cache"
      element={<ManageItemCache />}
      handle={{ crumb: "Manage Item Cache" }}
      errorElement={<RouteErrorBoundary />}
    />
    <Route
      path="manage-load-failed-deals"
      element={<ManageLoadFailedDeals />}
      handle={{ crumb: "Manage Load-Failed Deals" }}
      errorElement={<RouteErrorBoundary />}
    />
    {/* Any unmatched /dm/* → DM home */}
    <Route path="*" element={<Navigate to="/dm" replace />} />
  </Route>
);
